/**
 * PATH: packages/ui/src/components/settings/ShortcutCustomizer.tsx
 * FILE_ID: UI-SETTINGS-SHORTCUTCUSTOMIZER-001
 * PURPOSE: Presentational per-scope shortcut editor for Settings → Shortcuts.
 *          Lists every EDITABLE action grouped by scope and lets the operator
 *          remap (press-keys capture), enable/disable, reset one, or reset all.
 *          Surfaces the warn+allow conflict state inline (a remap that collides
 *          is permitted but flagged).
 * WHY: Phase 1 of the universal shortcuts rollout. Kept presentational (props +
 *      callbacks only, no API) so @quotech/ui stays API-free; the settings-hub
 *      layer wires it to useShortcutOverrides (which persists via /settings/ui/).
 *
 * FLOW:
 *   ┌────────────────────┐  ┌───────────────┐  ┌────────────────────────────┐
 *   │ SHORTCUT_ACTIONS    │→ │ + overrides   │→ │ grouped editable rows:       │
 *   │ (editable subset)   │  │ (props)       │  │ remap / toggle / reset       │
 *   └────────────────────┘  └───────────────┘  └────────────────────────────┘
 *
 * DEPENDENCIES:
 *   - ../../shortcuts: action model + pure fold/conflict + formatBinding
 *   - ./KeyCap: shared keycap pill
 *   - ./useKeychordCapture: press-keys capture state machine
 *   - ../../lib/utils: cn()
 */

import { useMemo } from 'react'
import { AlertTriangle, Keyboard, RotateCcw } from 'lucide-react'
import { cn } from '../../lib/utils'
import { KeyCap } from './KeyCap'
import { useKeychordCapture } from './useKeychordCapture'
import {
  SHORTCUT_ACTIONS,
  applyOverrides,
  findConflicts,
  formatBinding,
  getShortcutSection,
  type ShortcutAction,
  type ShortcutOverrideMap,
  type ShortcutPlatform,
} from '../../shortcuts'

export interface ShortcutCustomizerProps {
  /** Current per-user overrides (id → { binding?, enabled? }). */
  overrides: ShortcutOverrideMap
  /** Remap an action to a captured binding (null = unbound). */
  onRemap: (id: string, binding: string | null) => void
  /** Enable/disable an action without changing its binding. */
  onToggle: (id: string, enabled: boolean) => void
  /** Reset one action to its default (clears its override). */
  onReset: (id: string) => void
  /** Reset every customization. */
  onResetAll: () => void
  /** Action descriptors to edit. Defaults to all registered actions. */
  actions?: ShortcutAction[]
  /** Platform for keycap rendering + capture. Defaults to runtime detection. */
  platform?: ShortcutPlatform
  /** True while the first backend hydration is in flight. */
  loading?: boolean
  /** Non-null when load/save failed (rows stay usable from local state). */
  error?: string | null
  className?: string
}

function detectPlatform(): ShortcutPlatform {
  if (typeof navigator === 'undefined') return 'other'
  const probe = `${navigator.platform || ''} ${navigator.userAgent || ''}`
  return /mac|iphone|ipad|ipod/i.test(probe) ? 'mac' : 'other'
}

function scopeLabel(action: ShortcutAction): string {
  const s = action.scope
  if (s.kind === 'global') return 'Global'
  if (s.kind === 'hub') return getShortcutSection(s.hub)?.label ?? s.hub
  return s.screen
}

interface ScopeGroup {
  label: string
  actions: ShortcutAction[]
}

/** Group editable actions by scope label, preserving first-seen order. Pure. */
function groupByScope(actions: ShortcutAction[]): ScopeGroup[] {
  const order: string[] = []
  const byLabel = new Map<string, ShortcutAction[]>()
  for (const a of actions) {
    const label = scopeLabel(a)
    if (!byLabel.has(label)) {
      byLabel.set(label, [])
      order.push(label)
    }
    byLabel.get(label)!.push(a)
  }
  return order.map((label) => ({ label, actions: byLabel.get(label)! }))
}

export function ShortcutCustomizer({
  overrides,
  onRemap,
  onToggle,
  onReset,
  onResetAll,
  actions = SHORTCUT_ACTIONS,
  platform,
  loading = false,
  error = null,
  className,
}: ShortcutCustomizerProps) {
  const resolvedPlatform = platform ?? detectPlatform()

  const editable = useMemo(
    () => actions.filter((a) => a.editable !== false),
    [actions],
  )
  const groups = useMemo(() => groupByScope(editable), [editable])

  // Conflicts are computed across ALL actions (incl. locked ones like Escape)
  // so a remap that collides with a reserved key is still flagged.
  const conflicts = useMemo(
    () => findConflicts(applyOverrides(actions, overrides)),
    [actions, overrides],
  )
  const descById = useMemo(() => {
    const m: Record<string, string> = {}
    for (const a of actions) m[a.id] = a.description
    return m
  }, [actions])

  const { capturingId, start, cancel } = useKeychordCapture(resolvedPlatform, onRemap)
  const hasAnyOverride = Object.keys(overrides).length > 0

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Customize shortcuts</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Remap or turn off any shortcut. Changes sync across your devices.
          </p>
        </div>
        <button
          type="button"
          onClick={onResetAll}
          disabled={!hasAnyOverride}
          className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors',
            hasAnyOverride
              ? 'border-border text-muted-foreground hover:bg-accent hover:text-foreground'
              : 'border-border/60 text-muted-foreground/40 cursor-not-allowed',
          )}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset all
        </button>
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded-lg border border-border bg-card animate-pulse" />
          ))}
        </div>
      ) : editable.length === 0 ? (
        <div className="text-center py-10">
          <Keyboard className="w-9 h-9 mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm font-medium text-foreground">No customizable shortcuts yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            More shortcuts become editable as each hub is migrated.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.label} aria-label={`${group.label} shortcuts`}>
              <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {group.label}
              </h4>
              <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                {group.actions.map((action) => (
                  <ShortcutRow
                    key={action.id}
                    action={action}
                    override={overrides[action.id]}
                    platform={resolvedPlatform}
                    capturing={capturingId === action.id}
                    conflictWith={(conflicts[action.id] ?? []).map((id) => descById[id] ?? id)}
                    onStartCapture={() => start(action.id)}
                    onCancelCapture={cancel}
                    onToggle={(enabled) => onToggle(action.id, enabled)}
                    onReset={() => onReset(action.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

interface ShortcutRowProps {
  action: ShortcutAction
  override?: ShortcutOverrideMap[string]
  platform: ShortcutPlatform
  capturing: boolean
  conflictWith: string[]
  onStartCapture: () => void
  onCancelCapture: () => void
  onToggle: (enabled: boolean) => void
  onReset: () => void
}

function ShortcutRow({
  action,
  override,
  platform,
  capturing,
  conflictWith,
  onStartCapture,
  onCancelCapture,
  onToggle,
  onReset,
}: ShortcutRowProps) {
  const hasBindingOverride = override
    ? Object.prototype.hasOwnProperty.call(override, 'binding')
    : false
  const binding = hasBindingOverride ? override!.binding ?? null : action.defaultBinding
  const enabled = override?.enabled !== false
  const isCustomized = !!override
  const keys = binding ? formatBinding(binding, platform) : []

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-card">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className={cn('text-sm truncate', enabled ? 'text-foreground' : 'text-muted-foreground')}>
            {action.description}
          </p>
          {isCustomized && (
            <span className="text-[10px] font-medium text-primary uppercase tracking-wide shrink-0">
              Custom
            </span>
          )}
        </div>
        {conflictWith.length > 0 && enabled && binding && (
          <p className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            Same keys as {conflictWith.join(', ')}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {/* Remap / capture affordance */}
        {capturing ? (
          <button
            type="button"
            onClick={onCancelCapture}
            aria-live="polite"
            className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-primary bg-primary/10 text-[11px] font-medium text-primary"
          >
            Press keys…
            <KeyCap>Esc</KeyCap>
            <span className="text-muted-foreground">cancel</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onStartCapture}
            aria-label={`Change keys for ${action.description}`}
            className={cn(
              'inline-flex items-center gap-1 h-7 px-2 rounded-md border border-border hover:bg-accent transition-colors',
              !enabled && 'opacity-50',
            )}
          >
            {keys.length > 0 ? (
              keys.map((key, ki) => (
                <span key={ki} className="flex items-center gap-1">
                  {ki > 0 && <span className="text-muted-foreground text-xs">+</span>}
                  <KeyCap>{key}</KeyCap>
                </span>
              ))
            ) : (
              <span className="text-[11px] text-muted-foreground px-1">Unbound</span>
            )}
          </button>
        )}

        {/* Enable / disable switch */}
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`${enabled ? 'Disable' : 'Enable'} ${action.description}`}
          onClick={() => onToggle(!enabled)}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
            enabled ? 'bg-primary' : 'bg-muted',
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-background shadow transition-transform',
              enabled ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>

        {/* Reset to default */}
        <button
          type="button"
          onClick={onReset}
          disabled={!isCustomized}
          aria-label={`Reset ${action.description} to default`}
          className={cn(
            'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
            isCustomized
              ? 'text-muted-foreground hover:bg-accent hover:text-foreground'
              : 'text-muted-foreground/30 cursor-not-allowed',
          )}
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

export default ShortcutCustomizer
